import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult } from '../../core/models/api.models';
import { TestCaseDto, TestPlanDto, TestResultDto, TestRunDto } from './qa.models';

@Injectable({ providedIn: 'root' })
export class QaApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/test-plans';

  list(params: { projectId?: string; page?: number; pageSize?: number } = {}): Observable<PagedResult<TestPlanDto>> {
    return this.api.getPaged<TestPlanDto>(this.base, {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
      projectId: params.projectId
    });
  }

  getById(id: string): Observable<TestPlanDto> {
    return this.api.get<TestPlanDto>(`${this.base}/${id}`);
  }

  create(body: {
    projectId: string;
    name: string;
    description?: string;
    version?: string;
    isActive?: boolean;
  }): Observable<TestPlanDto> {
    return this.api.post<TestPlanDto>(this.base, body);
  }

  update(id: string, body: {
    projectId: string;
    name: string;
    description?: string;
    version?: string;
    isActive?: boolean;
  }): Observable<TestPlanDto> {
    return this.api.put<TestPlanDto>(`${this.base}/${id}`, body);
  }

  delete(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${id}`);
  }

  listCases(planId: string): Observable<TestCaseDto[]> {
    return this.api.get<TestCaseDto[]>(`${this.base}/${planId}/test-cases`);
  }

  createCase(planId: string, body: {
    title: string;
    description?: string;
    preconditions?: string;
    steps?: string;
    expectedResult?: string;
    status?: number;
    sortOrder?: number;
  }): Observable<TestCaseDto> {
    return this.api.post<TestCaseDto>(`${this.base}/${planId}/test-cases`, body);
  }

  updateCase(planId: string, caseId: string, body: {
    title: string;
    description?: string;
    preconditions?: string;
    steps?: string;
    expectedResult?: string;
    status?: number;
    sortOrder?: number;
  }): Observable<TestCaseDto> {
    return this.api.put<TestCaseDto>(`${this.base}/${planId}/test-cases/${caseId}`, body);
  }

  deleteCase(planId: string, caseId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${planId}/test-cases/${caseId}`);
  }

  createRun(planId: string, body: { name: string; executedById?: string; notes?: string }): Observable<TestRunDto> {
    return this.api.post<TestRunDto>(`${this.base}/${planId}/runs`, body);
  }

  addResult(planId: string, runId: string, body: {
    testCaseId: string;
    status?: number;
    executedById?: string;
    actualResult?: string;
    notes?: string;
  }): Observable<TestResultDto> {
    return this.api.post<TestResultDto>(`${this.base}/${planId}/runs/${runId}/results`, body);
  }

  completeRun(planId: string, runId: string): Observable<unknown> {
    return this.api.post(`${this.base}/${planId}/runs/${runId}/complete`, {});
  }
}
